import * as THREE from 'three';
import {
  cellCenter,
  createClearingMap,
  FLOES_CYCLE,
  FLOES_RADIUS,
  floesLayout,
  hexLen,
  hexToWorld,
  LAGOON_RADIUS,
  lagoonLayout,
  SEAL_RADIUS,
  sealLayout,
  type BattleState,
  type CazaPt,
  type LagoonLayout,
  type LagoonTrack,
  type MapDef,
} from '@somnhold/sim';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, looksRng, Precip, type OmenCtx } from '../omens/common.ts';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from '../models/boar.ts';
import { grayWolf } from '../models/caza.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome } from './snow.ts';
import { seaMat } from './bote.ts';
import { iceMaterial, iceSheetGeometry, releaseIceTexture } from './ice-sheet.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Long Hunt's chapter 4 places (docs/sendas/barbaro.md, «La Isla Blanca»): an island of rock under ice, still in the
 * sea at the edge of the titan's shadow; Sigrun's camp of piled stones on its shore, the stone with her strokes, and
 * behind it a frozen lagoon so clear the bottom shows. Every floor is a sheet of ice or of snow over rock with a ragged
 * edge (engine/ice-sheet.ts): never the board's hex tiles.
 *
 * - `islaShore` (the cinematic, the opening, the close): the shore by day (or at dawn), the camp of piled stones, the
 *   stone with thirty-one strokes, a broken fishing harpoon in frost, the lagoon north; at dawn, Sten's small boat
 *   rowing in by the channel.
 * - `islaFloes` (act 1): the shore with the camp and three floes off it, each joined by a neck of ice that cracks red
 *   three seconds and parts into water, and joins again; the crack through the hero marked red, open as dark water, shut
 *   again; Ivar's hunters far off on the ice hacking at it.
 * - `islaLagoon` (act 2; the diary, Ivar and the axe): the lagoon of clear ice with the tracks frozen under it (one
 *   canvas painted once: her boots with the tack's dot at the left heel, the Wolf's great paws, the pack's small ones;
 *   at each crossing the newer whole over a gap cut in the older), her six ends marked by shards of ice; cracks round a
 *   wrong end, the ice sunk at another, the crossings read glinting; found, the lagoon cracks whole and her last walk
 *   lights red under the ice end to end; the hole with the bag, the folded wolf's skin and her axe driven into the ice.
 * - `islaFire`: the fire on the snow under a green and red aurora, Bera alone with Terrón.
 * - `islaNight` (act 3, the memory; its dawn): the island by night under the aurora, Sigrun's camp whole round its fire
 *   (out for good when the Seal crosses it), the stone with thirty strokes, the pack's eyes in the dark at the rim, the
 *   Grey Wolf circling in the water; the open cracks inky; the Seal's line marked red, its slide, the ice it cut.
 *
 * Terrón is drawn here (the herd's body and legs). Draw calls: the dressing (one merged mesh), the sheets (snow, ice:
 * one each; the floes' three necks one each), the lagoon's tracks (in the ice's own texture) and its red last walk (one
 * plane), the sea, the sky, the aurora (one curtain), the crack and the Seal's line (a strip each), the glow layer, the
 * snow on the wind, Terrón (five). No lights of its own: the board's are repainted (HOURS).
 */

export const ISLA_PLACES = new Set(['islaShore', 'islaFloes', 'islaLagoon', 'islaFire', 'islaNight']);

/** The scenes' boards' seed (client game/cine.ts builds the same). */
export const ISLA_SCENE_SEED = 7414;

const S = WORLD_PER_HEX;
const K = S / 1000;
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const FIRE = new THREE.Color('#ff9a5a');
const FIRE_CORE = new THREE.Color('#fff0e6');
const COLD = new THREE.Color('#dfeaf6');
const AUR = new THREE.Color('#7dffc4');
const ROCK = ['#4f565f', '#5b636c', '#454b53', '#626a73'];
const STONE = ['#717a83', '#646d76', '#7d858d'];
const TERRON = new THREE.Color('#c9d6c4');

type Hour = 'day' | 'dusk' | 'night' | 'dawn';

const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; sea: [string, string, string]; dark: string; aurora: number }> = {
  // A cold bright day on the ice: white, pale blue, the rock dark under the snow.
  day: { bg: '#7d8b98', fog: ['#8f9caa', 60, 170], hemi: ['#e2e9f1', '#4a525c', 2.0], key: ['#f5f8fc', 1.7], rim: ['#a8bad0', 0.8], sky: ['#4d6178', '#a9b8c8', '#f1f5f9'], sea: ['#24465a', '#4f7488', '#e8f0f6'], dark: '#071219', aurora: 0 },
  // The low red sun on the ice (the ice kept cold under it).
  dusk: { bg: '#3a2a33', fog: ['#3b3a45', 40, 130], hemi: ['#c7d2e4', '#2e333b', 2.2], key: ['#ff9e80', 1.1], rim: ['#8fa0e0', 1.1], sky: ['#22202f', '#b35a48', '#f0b49a'], sea: ['#1d2a3a', '#42546a', '#dfe2ea'], dark: '#060a10', aurora: 0.15 },
  // Night under the aurora: moonlit and green-lit, never black; the ice reads.
  night: { bg: '#16202b', fog: ['#1a2430', 34, 110], hemi: ['#94aecb', '#151a22', 1.8], key: ['#c4daf2', 0.95], rim: ['#7cd6b0', 1.0], sky: ['#0b1220', '#183040', '#2f5a5a'], sea: ['#0f1c27', '#26404f', '#b8cad6'], dark: '#02060a', aurora: 1 },
  // A white dawn: pale, cold.
  dawn: { bg: '#c3ccd6', fog: ['#d0d8e1', 34, 130], hemi: ['#eef3f9', '#5b6470', 2.15], key: ['#fff3ea', 1.55], rim: ['#bcd0ea', 0.8], sky: ['#9aabbf', '#dbe3ec', '#ffffff'], sea: ['#3d5666', '#7a95a5', '#f4f8fb'], dark: '#0e1a22', aurora: 0.1 },
};

const hourOf = (place: string): Hour => (place === 'islaNight' || place === 'islaFire' ? 'night' : 'day');

/** The aurora: curtains of green with red at their tops, folding slowly; additive, behind the board to the north. */
const auroraMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: { time: { value: 0 }, k: { value: 1 } },
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    fog: false,
    vertexShader: /* glsl */ `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float k; varying vec2 vUv;
      float h(float x) { return fract(sin(x * 127.1) * 43758.5453); }
      float n(float x) { float i = floor(x), f = fract(x); return mix(h(i), h(i + 1.0), f * f * (3.0 - 2.0 * f)); }
      void main() {
        float x = vUv.x * 14.0;
        float fold = n(x * 0.6 + time * 0.05) * 0.35 + n(x * 1.7 - time * 0.08) * 0.15;
        float y = vUv.y - fold;
        float rays = 0.55 + 0.45 * n(x * 9.0 + time * 0.4) * n(x * 3.0 - time * 0.2);
        float band = smoothstep(0.0, 0.18, y) * (1.0 - smoothstep(0.35, 0.75, y));
        float top = smoothstep(0.32, 0.6, y) * (1.0 - smoothstep(0.6, 0.95, y));
        vec3 green = vec3(0.30, 1.0, 0.62);
        vec3 red = vec3(1.0, 0.26, 0.32);
        vec3 col = green * band * rays + red * top * rays * 0.75;
        float edge = smoothstep(0.0, 0.08, vUv.x) * (1.0 - smoothstep(0.92, 1.0, vUv.x));
        gl_FragColor = vec4(col * edge * k * 0.7, 1.0);
      }`,
  });

/* ---------- the scenes' grounds ---------- */

const cellsOf = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

/** The shore scene's island: its southern shore of rock and snow, the lagoon north; a channel to the south-east. */
const shoreFloor = (): { q: number; r: number }[] =>
  cellsOf(FLOES_RADIUS).filter((h) => {
    const p = hexToWorld(h.q, h.r);
    return !(p.x > 6000 && p.y > 6500);
  });

/** A scene's board (client game/cine.ts). */
export const islaSceneMap = (place: string): MapDef | null => {
  if (place === 'islaShore') {
    const m = createClearingMap(FLOES_RADIUS);
    const floor = new Set(shoreFloor().map((h) => `${h.q},${h.r}`));
    return { ...m, rocks: cellsOf(m.radius).filter((h) => !floor.has(`${h.q},${h.r}`)) };
  }
  if (place === 'islaFire') {
    const m = createClearingMap(8);
    return { ...m, rocks: cellsOf(m.radius).filter((h) => hexLen(h.q, h.r) > 5) };
  }
  if (place === 'islaLagoon') return { ...createClearingMap(LAGOON_RADIUS), rocks: lagoonLayout(ISLA_SCENE_SEED).rocks };
  if (place === 'islaNight') return { ...createClearingMap(SEAL_RADIUS), rocks: sealLayout(ISLA_SCENE_SEED).rocks };
  return null;
};

/* ---------- the lagoon's tracks, painted once ---------- */

/** The lagoon's canvas covers this square of the board (sim units), from its middle. */
const LAG_HALF = 9200;

const toPx = (v: number, n: number): number => ((v + LAG_HALF) / (LAG_HALF * 2)) * n;

/** Walks a track's points every `step` (sim), calling back with the point, its heading and the step's index. */
const along = (pts: CazaPt[], step: number, fn: (x: number, y: number, a: number, i: number) => void): void => {
  let i = 0;
  let carry = 0;
  for (let s = 1; s < pts.length; s++) {
    const a = pts[s - 1] as CazaPt;
    const b = pts[s] as CazaPt;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const L = Math.hypot(dx, dy);
    const ang = Math.atan2(dy, dx);
    for (let d = carry; d < L; d += step) fn(a.x + (dx * d) / L, a.y + (dy * d) / L, ang, i++);
    carry = (((carry - L) % step) + step) % step;
  }
};

/**
 * The tracks frozen under the lagoon's ice (§4): the oldest first; before each, a gap cut along its way in whatever lies
 * under it (the ice's own colour), so at a crossing the newer reads whole and the older cut. `red`: only her last walk,
 * red and bright (the found glow).
 */
const paintTracks = (L: LagoonLayout, n: number, red: number): HTMLCanvasElement => {
  const cv = document.createElement('canvas');
  cv.width = cv.height = n;
  const g = cv.getContext('2d') as CanvasRenderingContext2D;
  const px = n / (LAG_HALF * 2);
  if (red < 0) {
    // The clear ice: deep blue-green bottom, lighter shoals, stones on the bottom, bubbles caught in the ice.
    const gr = g.createRadialGradient(n / 2, n / 2, n * 0.05, n / 2, n / 2, n * 0.55);
    gr.addColorStop(0, '#6f9fb6');
    gr.addColorStop(0.7, '#8db7ca');
    gr.addColorStop(1, '#b9d3df');
    g.fillStyle = gr;
    g.fillRect(0, 0, n, n);
    const rnd = looksRng(0x1a60);
    for (let i = 0; i < 90; i++) {
      g.fillStyle = `rgba(${50 + rnd() * 30},${80 + rnd() * 30},${100 + rnd() * 30},${0.18 + rnd() * 0.2})`;
      g.beginPath();
      g.ellipse(rnd() * n, rnd() * n, 4 + rnd() * 18, 3 + rnd() * 12, rnd() * 3, 0, Math.PI * 2);
      g.fill();
    }
    for (let i = 0; i < 260; i++) {
      g.fillStyle = `rgba(255,255,255,${0.25 + rnd() * 0.35})`;
      g.beginPath();
      g.arc(rnd() * n, rnd() * n, 0.8 + rnd() * 2.2, 0, Math.PI * 2);
      g.fill();
    }
  }
  const order = L.tracks.map((t, i) => ({ t, i })).sort((a, b) => a.t.rank - b.t.rank);
  for (const { t } of order) {
    if (red >= 0 && !(t.who === 0 && t.letter === red)) continue;
    const big = t.who === 1;
    const small = t.who === 2;
    // The gap cut in what's under it.
    if (red < 0) {
      g.strokeStyle = '#7ea8bd';
      g.lineWidth = (big ? 640 : small ? 330 : 430) * px;
      g.lineCap = 'round';
      g.lineJoin = 'round';
      g.beginPath();
      t.pts.forEach((p, k) => (k ? g.lineTo(toPx(p.x, n), toPx(p.y, n)) : g.moveTo(toPx(p.x, n), toPx(p.y, n))));
      g.stroke();
    }
    const col = red >= 0 ? '#ff5a40' : big ? '#162a37' : small ? '#2c4d60' : '#1f3d52';
    g.fillStyle = col;
    if (t.who === 0) paintBoots(g, t, n, px, red >= 0);
    else
      along(t.pts, big ? 720 : 380, (x, y, a, i) => {
        const side = i % 2 ? 1 : -1;
        const ox = Math.cos(a + Math.PI / 2) * side * (big ? 170 : 90);
        const oy = Math.sin(a + Math.PI / 2) * side * (big ? 170 : 90);
        paw(g, toPx(x + ox, n), toPx(y + oy, n), a, (big ? 150 : 80) * px);
      });
  }
  return cv;
};

/** Her boots: an oval sole and heel, the left heel's tack a dot (light: it caught the frost). */
const paintBoots = (g: CanvasRenderingContext2D, t: LagoonTrack, n: number, px: number, red: boolean): void => {
  along(t.pts, 430, (x, y, a, i) => {
    const left = i % 2 === 0;
    const side = left ? -1 : 1;
    const cx = toPx(x + Math.cos(a + Math.PI / 2) * side * 120, n);
    const cy = toPx(y + Math.sin(a + Math.PI / 2) * side * 120, n);
    g.save();
    g.translate(cx, cy);
    g.rotate(a);
    g.globalAlpha = red ? 1 : 0.92;
    g.beginPath();
    g.ellipse(60 * px, 0, 95 * px, 52 * px, 0, 0, Math.PI * 2);
    g.fill();
    g.beginPath();
    g.ellipse(-95 * px, 0, 52 * px, 45 * px, 0, 0, Math.PI * 2);
    g.fill();
    if (left) {
      const was = g.fillStyle;
      g.fillStyle = red ? '#ffd2c4' : '#d8eaf2';
      g.beginPath();
      g.arc(-100 * px, 0, Math.max(1.2, 17 * px), 0, Math.PI * 2);
      g.fill();
      g.fillStyle = was;
    }
    g.restore();
  });
};

/** A paw: a pad and four toes ahead of it. */
const paw = (g: CanvasRenderingContext2D, x: number, y: number, a: number, r: number): void => {
  g.save();
  g.translate(x, y);
  g.rotate(a);
  g.beginPath();
  g.ellipse(0, 0, r * 0.62, r * 0.55, 0, 0, Math.PI * 2);
  g.fill();
  for (const [tx, ty] of [
    [0.85, -0.5],
    [1.05, -0.17],
    [1.05, 0.17],
    [0.85, 0.5],
  ] as const) {
    g.beginPath();
    g.ellipse(tx * r, ty * r * 1.1, r * 0.24, r * 0.2, 0, 0, Math.PI * 2);
    g.fill();
  }
  g.restore();
};

/* ---------- the layer ---------- */

interface Neck {
  m: THREE.Mesh;
  y: number;
  cells: CazaPt[];
}

export class IslaLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private texs: THREE.Texture[] = [];
  private glow: GlowLayer;
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private snow: Precip | null = null;
  private sea: THREE.ShaderMaterial | null = null;
  private aurora: THREE.ShaderMaterial | null = null;
  private auroraK = 0;
  private seaY = -0.62;
  private now = 0;
  private hour: Hour;
  private rnd = looksRng(0x15a1);
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number; fire?: boolean }[] = [];
  private embers: THREE.Vector3[] = [];
  private nextEmber = 0;
  private look = new THREE.Vector3();
  private v3 = new THREE.Vector3();
  private e3 = new THREE.Euler();
  private last: BattleState | null = null;
  private seed: number;
  private fight = false;
  private fireOut = false;
  // The floes (act 1).
  private necks: Neck[] = [];
  private crackStrip: THREE.Mesh | null = null;
  private crackKey = '';
  private hunters: THREE.Group | null = null;
  private nextChip = 0;
  // The lagoon (act 2).
  private lag: LagoonLayout | null = null;
  private redPlane: THREE.Mesh | null = null;
  private foundAt = -1;
  private ringsAt: THREE.Group[] = [];
  private sinks: THREE.Mesh[] = [];
  private bag: THREE.Group | null = null;
  private seenAt: { p: THREE.Vector3; t: number }[] = [];
  // Sigrun's last night (act 3).
  private slideStrip: THREE.Mesh | null = null;
  private cutStrip: THREE.Mesh | null = null;
  private cutKey = '';
  private gray: THREE.Group | null = null;
  private eyes: THREE.Vector3[] = [];
  private boat: THREE.Group | null = null;
  // Terrón.
  private tBody: THREE.Mesh | null = null;
  private tLegs: THREE.Mesh[] = [];
  private tTrack = { px: 0, py: 0, cx: 0, cy: 0, dist: 0, set: false };
  private mat: THREE.MeshStandardMaterial;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `isla-${place}`;
    this.hour = hourOf(place);
    this.seed = s.rules?.senda ? s.seed : ISLA_SCENE_SEED;
    this.glow = new GlowLayer(ctx.low ? 160 : 320);
    this.mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.86, metalness: 0.04, flatShading: true }));
    this.fight = !!s.rules?.senda?.isla;
    this.buildSea();
    const kit = new Kit();
    if (place === 'islaFloes') this.buildFloes(kit);
    else if (place === 'islaLagoon') this.buildLagoon(kit);
    else if (place === 'islaNight') this.buildNight(kit);
    else if (place === 'islaFire') this.buildFire(kit);
    else this.buildShore(kit);
    this.buildRim(kit);
    this.buildTerron();
    const dress = kit.build(this.mat, 'isla-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    if (!ctx.crypt) {
      this.snow = new Precip({ count: ctx.low ? 110 : 220, box: [16, 6, 16], fall: 0.5, wind: [2.4, 0.4], sway: 0.6, size: [0.03, 0.07], color: '#f2f6fa', opacity: 0.3, round: true }, 94);
      this.group.add(this.snow.mesh);
    }
    this.buildAurora();
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0, fire = false): void {
    this.lights.push({ p: p.clone(), c: color, size, a, seed: this.lights.length * 1.37, flick, ...(fire ? { fire } : {}) });
  }

  private sheet(cells: { q: number; r: number }[], o: { snow?: boolean; top?: number; seed?: number; topA?: string; topB?: string; side?: string; map?: THREE.Texture; uvBox?: boolean }): THREE.Mesh {
    const geo = this.track(
      iceSheetGeometry(cells, {
        seed: o.seed ?? this.seed,
        top: o.top ?? 0.02,
        foot: this.seaY - 0.8,
        rag: o.snow ? 300 : 230,
        topA: o.topA ?? (o.snow ? '#eef2f5' : '#e6eef4'),
        topB: o.topB ?? (o.snow ? '#c6cfd8' : '#b9cedd'),
        side: o.side ?? (o.snow ? '#6d7781' : '#93b1c6'),
      }),
    );
    if (o.uvBox) {
      // The lagoon's own canvas, laid once over its square (not tiled).
      const p = geo.getAttribute('position') as THREE.BufferAttribute;
      const uv = geo.getAttribute('uv') as THREE.BufferAttribute;
      for (let i = 0; i < p.count; i++) uv.setXY(i, (p.getX(i) / K + LAG_HALF) / (LAG_HALF * 2), 1 - (p.getZ(i) / K + LAG_HALF) / (LAG_HALF * 2));
      uv.needsUpdate = true;
    }
    const mat = o.map ? this.track(new THREE.MeshStandardMaterial({ vertexColors: true, map: o.map, roughness: 0.22, metalness: 0.06, side: THREE.DoubleSide })) : this.track(iceMaterial());
    if (!o.map) this.usesIce++;
    const m = new THREE.Mesh(geo, mat);
    m.receiveShadow = true;
    this.group.add(m);
    return m;
  }

  private usesIce = 0;

  private at(p: CazaPt, h = 0): THREE.Vector3 {
    return new THREE.Vector3(p.x * K, h, p.y * K);
  }

  private ownModel(root: THREE.Object3D): void {
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      if (m.geometry) this.geos.push(m.geometry);
      const mm = m.material as THREE.Material | THREE.Material[];
      for (const x of Array.isArray(mm) ? mm : [mm]) this.mats.push(x);
    });
  }

  private buildSea(): void {
    const sea = this.track(seaMat());
    this.sea = sea;
    this.paintSea();
    const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(300, 300, 1, 1)), sea);
    m.rotation.x = -Math.PI / 2;
    m.position.y = this.seaY;
    m.renderOrder = -2;
    this.group.add(m);
  }

  private paintSea(): void {
    const sea = this.sea;
    if (!sea) return;
    const look = HOURS[this.hour];
    const u = sea.uniforms;
    (u.deep as THREE.IUniform<THREE.Color>).value.set(look.sea[0]);
    (u.shallow as THREE.IUniform<THREE.Color>).value.set(look.sea[1]);
    (u.foamC as THREE.IUniform<THREE.Color>).value.set(look.sea[2]);
    (u.darkC as THREE.IUniform<THREE.Color>).value.set(look.dark);
    (u.rough as THREE.IUniform<number>).value = 0.12;
  }

  private buildAurora(): void {
    const m = this.track(auroraMat());
    this.aurora = m;
    const geo = this.track(new THREE.CylinderGeometry(95, 95, 46, 48, 1, true, Math.PI * 0.55, Math.PI * 0.9));
    const mesh = new THREE.Mesh(geo, m);
    mesh.position.y = 18;
    mesh.renderOrder = -1;
    mesh.frustumCulled = false;
    this.group.add(mesh);
  }

  /* ---------- the island's pieces ---------- */

  /** Rock and snow round the island's rim, out of the play (it's an island of rock under ice, high at the south). */
  private buildRim(kit: Kit): void {
    if (this.place === 'islaFire') return;
    // The black rock on the horizon to the north (where the Wolf goes): high, sheer, no snow on it.
    if (this.place === 'islaNight' || this.place === 'islaShore') {
      const rk = new THREE.DodecahedronGeometry(0.5, 0);
      const rn = looksRng(0xb1ac);
      for (let i = 0; i < 9; i++) kit.add(rk, i % 2 ? '#15181d' : '#1c2026', place(-14 + (rn() - 0.5) * 10, this.seaY + 2 + rn() * 5, -66 + (rn() - 0.5) * 6, rn() * 3, new THREE.Vector3(6 + rn() * 5, 6 + rn() * 9, 5 + rn() * 3)));
      rk.dispose();
    }
    // (A lumpy boulder, not a dodecahedron: seen from above a scaled dodecahedron read as a hex column, as the board's
    // grid; an icosahedron with its corners pushed in and out, tilted and off the cell's middle, reads as rock.)
    const rock = new THREE.IcosahedronGeometry(0.5, 1);
    const pos = rock.getAttribute('position') as THREE.BufferAttribute;
    const jit = looksRng(0x50c4);
    const seen = new Map<string, number>();
    for (let i = 0; i < pos.count; i++) {
      const k = `${pos.getX(i).toFixed(3)},${pos.getY(i).toFixed(3)},${pos.getZ(i).toFixed(3)}`;
      let f = seen.get(k);
      if (f === undefined) {
        f = 0.78 + jit() * 0.42;
        seen.set(k, f);
      }
      pos.setXYZ(i, pos.getX(i) * f, pos.getY(i) * f, pos.getZ(i) * f);
    }
    rock.computeVertexNormals();
    const R = this.map.radius;
    const rnd = looksRng(0x2b1 + this.seed);
    for (const h of cellsOf(R)) {
      const d = hexLen(h.q, h.r);
      if (d < R - 1) continue;
      if (rnd() < 0.55) continue;
      const p = hexToWorld(h.q, h.r);
      const south = p.y > 4000;
      const tall = (south ? 1.4 : 0.5) + rnd() * (south ? 2.2 : 0.9);
      const x = p.x * K * 1.04 + (rnd() - 0.5) * 1.4;
      const z = p.y * K * 1.04 + (rnd() - 0.5) * 1.4;
      const sx = 1.3 + rnd() * 1.4;
      const sz = 1.1 + rnd() * 1.3;
      kit.add(rock, ROCK[Math.floor(rnd() * 4)] as string, place(x, this.seaY + tall * 0.38, z, rnd() * 6, new THREE.Vector3(sx, tall, sz), (rnd() - 0.5) * 0.5, (rnd() - 0.5) * 0.5));
      // The snow lies on its windward top, a little off its middle.
      kit.add(rock, '#eef2f5', place(x + (rnd() - 0.5) * 0.4, this.seaY + tall * 0.78, z + (rnd() - 0.5) * 0.4, rnd() * 6, new THREE.Vector3(sx * 0.7, 0.32, sz * 0.7)));
      // A smaller stone beside it, now and then.
      if (rnd() < 0.5) kit.add(rock, ROCK[Math.floor(rnd() * 4)] as string, place(x + (rnd() - 0.5) * 2.4, this.seaY + 0.25, z + (rnd() - 0.5) * 2.4, rnd() * 6, 0.6 + rnd() * 0.6));
    }
    rock.dispose();
  }

  /** Sigrun's camp of piled stones: a low ring of piled stones, cairns, a lean-to of hides on poles, a rack. */
  private buildCamp(kit: Kit, at: CazaPt, whole: boolean): void {
    const rnd = looksRng(0x5167);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const box = new THREE.BoxGeometry(1, 1, 1);
    const cx = at.x * K;
    const cz = at.y * K;
    // The ring wall, a gap toward the lagoon.
    for (let i = 0; i < 34; i++) {
      const a = (i / 34) * Math.PI * 2;
      if (Math.abs(((a + Math.PI / 2) % (Math.PI * 2)) - Math.PI) < 0.45) continue;
      const rr = 2.2 + rnd() * 0.25;
      for (let k = 0; k < 2; k++) kit.add(rock, STONE[(i + k) % 3] as string, place(cx + Math.cos(a) * rr, 0.16 + k * 0.24, cz + Math.sin(a) * rr, rnd() * 3, new THREE.Vector3(0.5 + rnd() * 0.25, 0.32, 0.42 + rnd() * 0.2)));
      if (i % 3 === 0) kit.add(rock, '#f3f6f8', place(cx + Math.cos(a) * rr, 0.52, cz + Math.sin(a) * rr, rnd() * 3, new THREE.Vector3(0.45, 0.1, 0.4)));
    }
    // Cairns on the ring.
    for (const a of [0.4, 2.4, 4.1]) {
      for (let k = 0; k < 4; k++) kit.add(rock, STONE[k % 3] as string, place(cx + Math.cos(a) * 2.6, 0.15 + k * 0.28, cz + Math.sin(a) * 2.6, k, 0.42 - k * 0.06));
    }
    // The lean-to of hides on two poles (whole in the memory; fallen and frosted seventy years on).
    if (whole) {
      for (const k of [-1, 1]) kit.add(box, '#5c4a3a', place(cx - 1.2 + k * 0.7, 0.7, cz + 1.3, 0, new THREE.Vector3(0.07, 1.4, 0.07)));
      kit.add(box, '#9a8d80', place(cx - 1.2, 0.75, cz + 1.75, 0, new THREE.Vector3(1.7, 1.5, 0.05), -0.75, 0));
      kit.add(box, '#b3a89c', place(cx - 1.2, 1.28, cz + 1.3, 0, new THREE.Vector3(1.75, 0.12, 0.14)));
      kit.add(box, '#9a948e', place(cx - 1.2, 0.04, cz + 1.05, 0.2, new THREE.Vector3(1.4, 0.05, 1.0)));
      // A rack of skins and a fish.
      for (const k of [-1, 1]) kit.add(box, '#5c4a3a', place(cx + 1.3 + k * 0.5, 0.55, cz - 0.9, 0, new THREE.Vector3(0.06, 1.1, 0.06)));
      kit.add(box, '#5c4a3a', place(cx + 1.3, 1.08, cz - 0.9, 0, new THREE.Vector3(1.1, 0.06, 0.06)));
      kit.add(box, '#8f8a84', place(cx + 1.12, 0.78, cz - 0.9, 0, new THREE.Vector3(0.5, 0.6, 0.03)));
      kit.add(box, '#6f6b67', place(cx + 1.5, 0.82, cz - 0.9, 0, new THREE.Vector3(0.4, 0.5, 0.03)));
    } else {
      kit.add(box, '#4d4036', place(cx - 1.2, 0.08, cz + 1.3, 0.6, new THREE.Vector3(1.6, 0.08, 0.08)));
      kit.add(rock, '#eef2f5', place(cx - 1.0, 0.1, cz + 1.4, 0.3, new THREE.Vector3(1.6, 0.18, 1.0)));
    }
    // The fire's ring of stones.
    for (let i = 0; i < 9; i++) {
      const a = (i / 9) * Math.PI * 2;
      kit.add(rock, '#4a5058', place(cx + Math.cos(a) * 0.42, 0.08, cz + Math.sin(a) * 0.42, i, 0.2));
    }
    if (whole) for (let i = 0; i < 4; i++) kit.add(box, '#4a3c30', place(cx, 0.12, cz, (i * Math.PI) / 4, new THREE.Vector3(0.7, 0.08, 0.08)));
    rock.dispose();
    box.dispose();
  }

  /** The stone with her strokes: a standing slab, the strokes cut white in it (thirty-one; thirty the night before). */
  private buildStrokes(kit: Kit, at: CazaPt, n: number): void {
    const x = at.x * K;
    const z = at.y * K;
    kit.add(new THREE.BoxGeometry(1, 1, 1), '#5f666e', place(x, 0.45, z, 0.3, new THREE.Vector3(1.3, 0.9, 0.3)));
    kit.add(new THREE.DodecahedronGeometry(0.5, 0), '#f1f4f7', place(x, 0.93, z, 0.3, new THREE.Vector3(1.3, 0.14, 0.4)));
    const c = Math.cos(0.3);
    const sn = Math.sin(0.3);
    for (let i = 0; i < n; i++) {
      const row = Math.floor(i / 8);
      const col = i % 8;
      // A fifth of each group crossed (as one counts days).
      const lx = -0.5 + col * 0.13;
      const ly = 0.72 - row * 0.17;
      kit.add(new THREE.BoxGeometry(0.025, 0.13, 0.02), '#e6ebef', place(x + lx * c, ly, z - lx * sn + 0.16 * c, 0.3, 1, 0, col === 7 ? 1.1 : 0.05));
    }
  }

  /** A broken fishing harpoon in frost, and a few bones. */
  private buildHarpoon(kit: Kit, at: CazaPt): void {
    const x = at.x * K;
    const z = at.y * K;
    kit.add(new THREE.CylinderGeometry(0.03, 0.03, 1.4, 5), '#6a5846', place(x, 0.06, z, 0.7, 1, Math.PI / 2, 0));
    kit.add(new THREE.ConeGeometry(0.06, 0.3, 4), '#6f757c', place(x + 0.75 * Math.sin(0.7), 0.06, z + 0.75 * Math.cos(0.7), 0.7, 1, Math.PI / 2, 0));
    kit.add(new THREE.CylinderGeometry(0.03, 0.03, 0.5, 5), '#6a5846', place(x - 0.6, 0.05, z + 0.4, 2.1, 1, Math.PI / 2, 0));
    kit.add(new THREE.DodecahedronGeometry(0.5, 0), '#f4f7f9', place(x, 0.06, z, 0.7, new THREE.Vector3(1.2, 0.06, 0.4)));
  }

  /** A small boat (Sten's, or the clan's): six oars, shields, a boar's head; `red`, Ivar's prow. */
  private smallBoat(kit: Kit, x: number, z: number, yaw: number, red: boolean): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const c = Math.cos(yaw);
    const s = Math.sin(yaw);
    const P = (lx: number, ly: number, lz: number, sc: THREE.Vector3, tz = 0): THREE.Matrix4 => place(x + lx * c + lz * s, ly, z - lx * s + lz * c, yaw, sc, 0, tz);
    for (let i = 0; i < 9; i++) {
      const t = i / 8 - 0.5;
      const w = 0.6 * Math.cos(t * Math.PI * 0.92) + 0.04;
      const rise = Math.pow(Math.abs(t) * 2, 3) * 0.3;
      kit.add(box, '#3b3029', P(0, this.seaY + 0.1 + rise, t * 4.0, new THREE.Vector3(w * 1.3, 0.12, 0.5)));
      for (const k of [-1, 1]) kit.add(box, i % 2 ? '#4b3d33' : '#54453a', P(k * w * 0.72, this.seaY + 0.32 + rise, t * 4.0, new THREE.Vector3(0.07, 0.4, 0.52), -k * 0.32));
    }
    kit.add(new THREE.DodecahedronGeometry(0.5, 0), red ? '#b23a2a' : '#5d544c', P(0, this.seaY + 0.85, -2.2, new THREE.Vector3(0.32, 0.34, 0.5)));
    for (const k of [-1, 1]) kit.add(box, '#6a5846', P(k * 1.1, this.seaY + 0.3, -0.4, new THREE.Vector3(1.6, 0.04, 0.05), k * 0.25));
    box.dispose();
  }

  /* ---------- the places ---------- */

  private buildShore(kit: Kit): void {
    // The island's shore of rock and snow to the south; the lagoon north, clear (no tracks in this light).
    const floor = shoreFloor();
    const shore = floor.filter((h) => hexToWorld(h.q, h.r).y >= 2200);
    const lag = floor.filter((h) => hexToWorld(h.q, h.r).y < 2200);
    this.sheet(shore, { snow: true });
    this.sheet(lag, { topA: '#dceaf2', topB: '#9fc2d4', seed: this.seed + 3 });
    this.buildCamp(kit, { x: 500, y: 5400 }, false);
    this.buildStrokes(kit, { x: 1900, y: 5200 }, 31);
    this.buildHarpoon(kit, { x: -800, y: 3800 });
    // Ivar's hunters' holes in the lagoon, chips of ice round them.
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    for (const [x, y] of [
      [500, -5600],
      [3800, -6400],
      [-2600, -6600],
    ] as const) {
      kit.add(new THREE.CylinderGeometry(0.55, 0.55, 0.04, 10), '#1c3646', place(x * K, 0.04, y * K));
      for (let i = 0; i < 8; i++) kit.add(rock, '#e8f0f5', place(x * K + Math.cos(i) * 0.8, 0.07, y * K + Math.sin(i * 1.7) * 0.8, i, 0.18));
    }
    rock.dispose();
    // At dawn, Sten's small boat in the channel to the south-east (shown in the close).
    const bk = new Kit();
    this.smallBoat(bk, 9.5 * S, 9.2 * S, 2.3, false);
    const bm = bk.build(this.mat, 'isla-boat');
    if (bm) {
      bm.castShadow = true;
      this.geos.push(bm.geometry);
      this.boat = new THREE.Group();
      this.boat.add(bm);
      this.boat.visible = false;
      this.group.add(this.boat);
    }
  }

  private buildFire(kit: Kit): void {
    this.sheet(cellsOf(5), { snow: true });
    // A low wall of snow blocks behind, the skins.
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    for (let i = 0; i < 12; i++) {
      const a = Math.PI * 0.9 + (i / 12) * Math.PI * 1.2;
      kit.add(rock, i % 2 ? '#c9d3dc' : '#b6c2cd', place(Math.cos(a) * 3.3 * S, 0.25, Math.sin(a) * 3.3 * S, i, new THREE.Vector3(0.9, 0.55, 0.6)));
    }
    kit.add(new THREE.PlaneGeometry(1.6, 1.1), '#7d7670', place(-0.9 * S, 0.05, 1.0 * S, 0.4, 1, -Math.PI / 2, 0));
    rock.dispose();
  }

  private buildFloes(kit: Kit): void {
    const L = floesLayout(this.seed, FLOES_CYCLE);
    this.sheet(L.shore, { snow: true });
    L.floeCells.forEach((cells, k) => this.sheet(cells, { seed: this.seed + 11 + k }));
    L.necks.forEach((cells, k) => {
      const m = this.sheet(cells, { seed: this.seed + 21 + k, topA: '#dbe7ef', topB: '#aac4d6' });
      this.necks.push({ m, y: 0, cells: cells.map((h) => hexToWorld(h.q, h.r)) });
    });
    this.buildCamp(kit, L.camp, false);
    this.buildStrokes(kit, { x: L.camp.x + 2600, y: L.camp.y - 600 }, 31);
    this.buildHarpoon(kit, { x: L.camp.x - 2800, y: L.camp.y - 900 });
    // Ivar's hunters far off on the ice to the north-east, hacking at it (dark figures, chips flying).
    const hk = new Kit();
    hk.add(new THREE.CylinderGeometry(1.1, 1.3, 0.3, 9), '#cfdde8', place(0, -0.1, 0));
    for (const [x, z] of [
      [-0.5, 0.2],
      [0.6, -0.3],
    ] as const) {
      hk.add(new THREE.CylinderGeometry(0.16, 0.2, 0.9, 7), '#2c3038', place(x, 0.5, z));
      hk.add(new THREE.SphereGeometry(0.13, 7, 6), '#3a3f47', place(x, 1.05, z));
      hk.add(new THREE.BoxGeometry(0.05, 0.7, 0.05), '#5c4a3a', place(x + 0.2, 0.9, z, 0, 1, 0, 0.6));
    }
    const hm = hk.build(this.mat, 'isla-hunters');
    if (hm) {
      this.geos.push(hm.geometry);
      this.hunters = new THREE.Group();
      this.hunters.add(hm);
      this.hunters.position.set(11.5 * S, this.seaY + 0.3, -9.5 * S);
      this.group.add(this.hunters);
    }
    // The crack's strip (built on its event).
    this.crackStrip = new THREE.Mesh(this.track(new THREE.BufferGeometry()), this.track(new THREE.MeshBasicMaterial({ color: '#0f2633', transparent: true, opacity: 0.95, depthWrite: false })));
    this.crackStrip.renderOrder = 3;
    this.crackStrip.visible = false;
    this.group.add(this.crackStrip);
  }

  private buildLagoon(kit: Kit): void {
    const L = lagoonLayout(this.seed);
    this.lag = L;
    const n = this.ctx.low ? 1024 : 2048;
    const tex = new THREE.CanvasTexture(paintTracks(L, n, -1));
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 4;
    this.texs.push(tex);
    const iceKeys = new Set(L.ice.map((h) => `${h.q},${h.r}`));
    const floor = cellsOf(this.map.radius).filter((h) => !(this.map.rocks ?? []).some((r) => r.q === h.q && r.r === h.r));
    const shore = floor.filter((h) => !iceKeys.has(`${h.q},${h.r}`));
    this.sheet(L.ice, { map: tex, uvBox: true, topA: '#ffffff', topB: '#e4eef4', side: '#8fb0c6' });
    this.sheet(shore, { snow: true, top: 0.05 });
    // Her last walk, red under the ice: shown when found (its own plane over the lagoon, additive).
    const rtex = new THREE.CanvasTexture(paintTracks(L, 1024, L.last));
    rtex.colorSpace = THREE.SRGBColorSpace;
    this.texs.push(rtex);
    const rp = new THREE.Mesh(this.track(new THREE.PlaneGeometry(LAG_HALF * 2 * K, LAG_HALF * 2 * K).rotateX(-Math.PI / 2)), this.track(new THREE.MeshBasicMaterial({ map: rtex, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false })));
    rp.position.y = 0.05;
    rp.renderOrder = 4;
    rp.visible = false;
    this.redPlane = rp;
    this.group.add(rp);
    // Her six ends: a few shards of ice standing up, frost round them.
    const shard = new THREE.ConeGeometry(0.12, 0.5, 4);
    L.ends.forEach((e, k) => {
      for (let i = 0; i < 4; i++) {
        const a = i * 1.7 + k;
        kit.add(shard, i % 2 ? '#dff0f8' : '#bfdcea', place(e.x * K + Math.cos(a) * 0.45, 0.2, e.y * K + Math.sin(a) * 0.45, a, new THREE.Vector3(1, 0.7 + (i % 3) * 0.3, 1), 0.2 * Math.cos(a), 0.2 * Math.sin(a)));
      }
      // The cracks round it (shown on a wrong break) and the open water (shown when it sinks).
      const ring = new THREE.Group();
      const crackMat = this.track(new THREE.MeshBasicMaterial({ color: '#2a5672', transparent: true, opacity: 0.9, depthWrite: false }));
      const rr = looksRng(900 + k);
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2 + rr() * 0.4;
        const len = 1.0 + rr() * 1.2;
        const c = new THREE.Mesh(this.track(new THREE.PlaneGeometry(0.05, len).rotateX(-Math.PI / 2).translate(0, 0, -len / 2 - 0.3)), crackMat);
        c.rotation.y = a;
        ring.add(c);
      }
      ring.position.set(e.x * K, 0.06, e.y * K);
      ring.visible = false;
      ring.renderOrder = 3;
      this.ringsAt.push(ring);
      this.group.add(ring);
      const sink = new THREE.Mesh(this.track(new THREE.CircleGeometry(1.35 * S, 20).rotateX(-Math.PI / 2)), this.track(new THREE.MeshBasicMaterial({ color: '#0b1c26', transparent: true, opacity: 0.94, depthWrite: false })));
      sink.position.set(e.x * K, 0.07, e.y * K);
      sink.visible = false;
      sink.renderOrder = 3;
      this.sinks.push(sink);
      this.group.add(sink);
    });
    shard.dispose();
    // The hole broken where her last walk ends, the bag of hide in it, the folded skin, her axe in the ice below: at
    // the end in the fight (when found), in the middle in the scenes.
    const fight = this.fight;
    const at = fight ? (L.ends[L.last] as CazaPt) : { x: 0, y: 0 };
    const bg = new THREE.Group();
    const bk = new Kit();
    bk.add(new THREE.CylinderGeometry(0.75, 0.6, 0.06, 12), '#102a38', place(0, 0.05, 0));
    for (let i = 0; i < 12; i++) bk.add(new THREE.DodecahedronGeometry(0.5, 0), i % 2 ? '#e2eef5' : '#c4dcea', place(Math.cos(i * 0.52) * 0.85, 0.08, Math.sin(i * 0.52) * 0.85, i, new THREE.Vector3(0.35, 0.12, 0.25)));
    bk.add(new THREE.SphereGeometry(0.22, 9, 7), '#6b5644', place(-0.55, 0.16, 0.65, 0, new THREE.Vector3(1.2, 0.75, 1)));
    bk.add(new THREE.BoxGeometry(0.06, 0.06, 0.3), '#3d3128', place(-0.55, 0.32, 0.65));
    bk.add(new THREE.BoxGeometry(0.7, 0.1, 0.5), '#8a8d92', place(0.6, 0.08, 0.7, 0.3));
    bk.add(new THREE.BoxGeometry(0.66, 0.04, 0.46), '#a6a9ad', place(0.6, 0.14, 0.7, 0.3));
    // The axe, driven into the ice below: haft up out of the water, its head under.
    bk.add(new THREE.CylinderGeometry(0.03, 0.035, 1.1, 6), '#5c4a3a', place(0.1, 0.35, -0.1, 0, 1, 0.25, 0.15));
    bk.add(new THREE.BoxGeometry(0.05, 0.22, 0.26), '#566069', place(0.03, -0.08, -0.2, 0, 1, 0.25, 0.15));
    bk.add(new THREE.SphereGeometry(0.045, 6, 5), '#a43a2c', place(0.15, 0.62, -0.02));
    const bm = bk.build(this.mat, 'isla-bag');
    if (bm) {
      bm.castShadow = true;
      this.geos.push(bm.geometry);
      bg.add(bm);
    }
    bg.position.set(at.x * K, 0, at.y * K);
    bg.visible = !fight;
    this.bag = bg;
    this.group.add(bg);
    this.light(new THREE.Vector3(at.x * K, 0.5, at.y * K), COLD, 2.4, fight ? 0 : 0.18);
  }

  private buildNight(kit: Kit): void {
    const L = sealLayout(this.seed);
    const floor = cellsOf(this.map.radius).filter((h) => !(this.map.rocks ?? []).some((r) => r.q === h.q && r.r === h.r));
    // The island's ice under the moon, the rim snowed over.
    const outer = floor.filter((h) => hexLen(h.q, h.r) >= SEAL_RADIUS - 1);
    const inner = floor.filter((h) => hexLen(h.q, h.r) < SEAL_RADIUS - 1);
    this.sheet(inner, { topA: '#b9cbdb', topB: '#6f8ea6' });
    this.sheet(outer, { snow: true, top: 0.05, seed: this.seed + 5, topA: '#cfd8e0', topB: '#8f9caa' });
    this.buildCamp(kit, L.fire, true);
    this.buildStrokes(kit, { x: L.fire.x + 2400, y: L.fire.y + 500 }, 30);
    // Her fire: core, flame, its light thrown wide on the ice.
    const f = this.at(L.fire, 0.4);
    this.light(f, FIRE_CORE, 0.9, 0.95, 1, true);
    this.light(f.clone().setY(0.65), FIRE, 2.2, 0.75, 1, true);
    this.light(f.clone().setY(0.1), FIRE, 6.0, 0.24, 0.5, true);
    this.embers.push(f);
    // The open cracks: rims of broken ice round them.
    const shard = new THREE.DodecahedronGeometry(0.5, 0);
    L.holes.forEach((p, k) => {
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2 + k;
        kit.add(shard, i % 2 ? '#dbe8f1' : '#b8d0df', place(p.x * K + Math.cos(a) * 0.95 * S, 0.06, p.y * K + Math.sin(a) * 0.95 * S, a, new THREE.Vector3(0.5, 0.16, 0.35)));
      }
    });
    shard.dispose();
    if (this.sea) {
      const dp = (this.sea.uniforms.darkP as THREE.IUniform<THREE.Vector3[]>).value;
      L.holes.forEach((p, i) => (dp[i] as THREE.Vector3).set(p.x * K, p.y * K, 0));
    }
    // The pack's eyes in the dark at the rim.
    const rnd = looksRng(0xe1e5);
    for (let i = 0; i < 12; i++) {
      const a = rnd() * Math.PI * 2;
      const r = (SEAL_RADIUS + 1.4 + rnd() * 2) * S;
      this.eyes.push(new THREE.Vector3(Math.cos(a) * r, 0.45 + rnd() * 0.3, Math.sin(a) * r));
    }
    // The Grey Wolf circling in the water, not coming in.
    const gw = grayWolf({});
    gw.root.scale.setScalar(SCALE.enemy * 2.3);
    gw.root.position.y = -0.8;
    this.ownModel(gw.root);
    this.gray = new THREE.Group();
    this.gray.add(gw.root);
    this.group.add(this.gray);
    // The Seal's line (marked red) and the ice it cut.
    this.slideStrip = new THREE.Mesh(this.track(new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2).translate(0, 0, -0.5)), this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.4, depthWrite: false })));
    this.slideStrip.renderOrder = 4;
    this.slideStrip.visible = false;
    this.group.add(this.slideStrip);
    this.cutStrip = new THREE.Mesh(this.track(new THREE.BufferGeometry()), this.track(new THREE.MeshBasicMaterial({ color: '#12303f', transparent: true, opacity: 0.9, depthWrite: false })));
    this.cutStrip.renderOrder = 3;
    this.cutStrip.visible = false;
    this.group.add(this.cutStrip);
  }

  /** A jagged dark strip through points (the crack's water, the ice the Seal cut): one quad strip, its edges ragged. */
  private strip(m: THREE.Mesh, pts: THREE.Vector3[], w: number, seed: number): void {
    const rnd = looksRng(seed);
    const pos: number[] = [];
    for (let i = 0; i + 1 < pts.length; i++) {
      const a = pts[i] as THREE.Vector3;
      const b = pts[i + 1] as THREE.Vector3;
      const d = b.clone().sub(a);
      const nx = -d.z / (d.length() || 1);
      const nz = d.x / (d.length() || 1);
      const wa = w * (0.6 + rnd() * 0.6);
      const wb = w * (0.6 + rnd() * 0.6);
      pos.push(a.x + nx * wa, 0.06, a.z + nz * wa, a.x - nx * wa, 0.06, a.z - nz * wa, b.x + nx * wb, 0.06, b.z + nz * wb);
      pos.push(a.x - nx * wa, 0.06, a.z - nz * wa, b.x - nx * wb, 0.06, b.z - nz * wb, b.x + nx * wb, 0.06, b.z + nz * wb);
    }
    m.geometry.dispose();
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    this.geos.push(g);
    m.geometry = g;
  }

  /* ---------- Terrón ---------- */

  private buildTerron(): void {
    const mat = this.track(new THREE.MeshStandardMaterial({ color: TERRON, roughness: 0.88, flatShading: true }));
    this.tBody = new THREE.Mesh(this.track(boarBody(13)), mat);
    this.tBody.castShadow = true;
    this.tBody.visible = false;
    this.group.add(this.tBody);
    const leg = this.track(boarLeg());
    for (let i = 0; i < 4; i++) {
      const m = new THREE.Mesh(leg, mat);
      m.visible = false;
      this.tLegs.push(m);
      this.group.add(m);
    }
  }

  private drawTerron(s: BattleState | null, alpha: number): void {
    const body = this.tBody;
    if (!body) return;
    const tn = s?.senda?.npcs.find((x) => x.id === 'terron' && x.hp > 0);
    let x: number;
    let y: number;
    let facing: number;
    let moving = false;
    let lie = false;
    if (tn) {
      const t = this.tTrack;
      x = t.set ? t.px + (t.cx - t.px) * alpha : tn.x;
      y = t.set ? t.py + (t.cy - t.py) * alpha : tn.y;
      facing = tn.facing;
      moving = tn.moving;
    } else {
      const fig = s?.heroes.find((h) => h.face === 'terron');
      const p = fig ? this.ctx.pos(fig.id) : undefined;
      if (!fig || !p) {
        body.visible = false;
        for (const l of this.tLegs) l.visible = false;
        return;
      }
      x = p.x / K;
      y = p.z / K;
      facing = fig.facing ?? 0;
      lie = this.place === 'islaFire' || this.place === 'islaLagoon';
    }
    const sc = SCALE.hero * 1.2;
    const g = this.ctx.at(x, y, lie ? -0.08 : 0);
    const stride = moving ? this.tTrack.dist * 9.5 : 0;
    const bob = moving ? Math.abs(Math.sin(stride)) * 0.025 : Math.max(0, Math.sin(this.now * 0.9)) * 0.01;
    const yaw = facingToRotY(facing);
    this.e3.set(0, yaw, Math.sin(stride) * 0.03, 'YXZ');
    body.position.set(g.x, g.y + bob, g.z);
    body.quaternion.setFromEuler(this.e3);
    body.scale.setScalar(sc);
    body.visible = true;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    this.tLegs.forEach((l, i) => {
      const [lx, lz] = BOAR_LEGS[i] as [number, number];
      const ph = i === 0 || i === 3 ? 1 : -1;
      this.e3.set(lie ? 1.2 * ph : Math.sin(stride) * 0.5 * ph, yaw, 0, 'YXZ');
      l.quaternion.setFromEuler(this.e3);
      l.position.set(g.x + (lx * cy + lz * sy) * sc, g.y + BOAR_HIP_Y * sc + bob, g.z + (-lx * sy + lz * cy) * sc);
      l.scale.setScalar(sc);
      l.visible = true;
    });
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    this.terrain = terrain;
    // Every floor here is a sheet of its own: the board's hex tiles are not drawn at all.
    terrain.mesh.visible = false;
    terrain.decor.traverse((o) => {
      if (o.name === 'roads' || o.name === 'outskirts-ground') o.visible = false;
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    if (terrain.fog) terrain.fog.group.visible = false;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint();
  }

  /** The board's light by the hour (a scene's sky, when it gives one). */
  paint(sky?: 'day' | 'dusk' | 'night' | 'dawn'): void {
    if (sky) this.hour = sky;
    const look = HOURS[this.hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(this.hour === 'dusk' ? 0.3 : -0.4, this.hour === 'dusk' ? 0.06 : this.hour === 'dawn' ? 0.14 : this.hour === 'night' ? -0.3 : 0.35, this.hour === 'dawn' ? 0.9 : -0.9);
    this.paintSea();
    if (this.boat) this.boat.visible = this.place === 'islaShore' && this.hour === 'dawn';
    this.auroraK = look.aurora;
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.last = s;
    const t = s.senda?.npcs.find((n) => n.id === 'terron');
    if (t) {
      const k = this.tTrack;
      if (!k.set) Object.assign(k, { px: t.x, py: t.y, cx: t.x, cy: t.y, dist: 0, set: true });
      else {
        k.dist += Math.hypot(t.x - k.cx, t.y - k.cy) * K;
        k.px = k.cx;
        k.py = k.cy;
        k.cx = t.x;
        k.cy = t.y;
      }
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.1);
      switch (e.kind) {
        case 'iNeckPart':
        case 'iCrackOpen': {
          this.splash(p, 1.0);
          this.ctx.shake(0.18);
          const n = this.necks[e.n ?? -1];
          if (e.kind === 'iNeckPart' && n) for (const c of n.cells) if (this.rnd() < 0.5) this.splash(this.ctx.at(c.x, c.y, 0), 0.5);
          break;
        }
        case 'iNeckJoin':
          this.ctx.vfx.burst(p.setY(0.2), { n: 12, color: '#e8f0f6', speed: 0.04, up: 0.05, size: 0.12, life: 20, gravity: 0.006 });
          break;
        case 'iFall':
        case 'iDrown':
          this.splash(p, e.kind === 'iFall' ? 1.1 : 0.7);
          break;
        case 'iCampHit':
        case 'iBreak':
          this.ctx.vfx.burst(p.setY(0.3), { n: 10, color: e.kind === 'iBreak' ? '#e6f0f6' : '#8a9198', speed: 0.05, up: 0.07, size: 0.1, life: 18, gravity: 0.008 });
          if (e.kind === 'iBreak') this.ctx.shake(0.08);
          break;
        case 'iSeen':
          this.seenAt.push({ p: p.clone().setY(0.2), t: this.now });
          this.ctx.vfx.ring(p.clone().setY(0.08), 0.3, 1.8, '#eaf6ff', 18, 0.9);
          break;
        case 'iCrackRing': {
          const r = this.ringsAt[e.n ?? -1];
          if (r) r.visible = true;
          this.ctx.shake(0.12);
          break;
        }
        case 'iSink': {
          const m = this.sinks[e.n ?? -1];
          if (m) m.visible = true;
          const r = this.ringsAt[e.n ?? -1];
          if (r) r.visible = false;
          this.splash(p, 1.6);
          this.ctx.shake(0.3);
          break;
        }
        case 'iFound':
          this.foundAt = this.now;
          if (this.bag) this.bag.visible = true;
          if (this.redPlane) this.redPlane.visible = true;
          this.ctx.shake(0.45);
          this.ctx.flare(p.clone().setY(1.0), '#ff8a6e', 7, 50);
          for (let i = 0; i < 6; i++) this.ctx.vfx.ring(p.clone().setY(0.08), 0.4, 4 + i * 3, '#ffb9a6', 22 + i * 6, 0.8);
          break;
        case 'iFireHit':
          this.ctx.vfx.burst(p.setY(0.4), { n: 10, color: '#ff9a5a', speed: 0.05, up: 0.08, size: 0.07, life: 20, gravity: 0.004 });
          break;
        case 'iFireOut':
          this.fireOut = true;
          this.ctx.vfx.burst(p.setY(0.4), { n: 26, color: '#4a4f57', speed: 0.03, up: 0.06, size: 0.4, life: 50, smoke: true });
          break;
        case 'iAim':
          this.ctx.vfx.ring(p.clone().setY(0.1), 0.3, 1.6, '#ff4f3a', 14, 0.9);
          break;
        case 'iSlide':
          this.ctx.shake(0.22);
          this.ctx.vfx.burst(p.setY(0.3), { n: 18, color: '#e8f0f6', speed: 0.07, up: 0.08, size: 0.14, life: 22, gravity: 0.008 });
          break;
        case 'iLook':
          this.ctx.vfx.ring(p.clone().setY(0.12), 2.4, 0.4, '#ff4f3a', 20, 0.95);
          break;
        case 'iDive':
          this.splash(p, 1.3);
          break;
        case 'iRise':
          this.splash(p, 1.0);
          this.ctx.vfx.burst(p.clone().setY(0.5), { n: 14, color: '#3a3f47', speed: 0.03, up: 0.06, size: 0.2, life: 26, smoke: true });
          break;
        case 'iCut':
          this.ctx.vfx.ring(p.clone().setY(0.3), 0.3, 3.2, '#ff8a6e', 16, 0.95);
          this.ctx.shake(0.12);
          break;
        case 'iRear':
          this.ctx.vfx.ring(p.clone().setY(0.2), 0.3, 1.8, '#ff4f3a', 18, 0.8);
          break;
        case 'iHowlCut':
          this.ctx.vfx.ring(p.clone().setY(0.3), 0.3, 2.4, '#ff8a6e', 14, 0.9);
          break;
      }
    }
  }

  private splash(p: THREE.Vector3, big: number): void {
    this.ctx.vfx.burst(p.clone().setY(this.seaY + 0.4), { n: Math.round(20 * big), color: '#eef3f8', speed: 0.05 * big, up: 0.15 * big, size: 0.18 * big, life: 30, gravity: 0.008, spread: 0.6 });
    this.ctx.vfx.ring(p.clone().setY(0.05), 0.4, 2.6 * big, '#e6eef6', 18, 0.8);
  }

  draw(s: BattleState): void {
    this.last = s;
  }

  /** Act 1, each frame: the necks cracking red, parting into the water, joining; the crack under the hero. */
  private frameFloes(now: number, dt: number): void {
    const s = this.last;
    const r = s?.rules?.senda?.isla?.floes;
    const w = s?.senda?.isla?.floes;
    if (!s || !r || !w) return;
    const pulse = 0.5 + 0.5 * Math.sin(now * 12);
    this.necks.forEach((n, k) => {
      const st = w.necks[k] ?? 0;
      const ty = st === 2 ? this.seaY - 0.6 : 0;
      n.y += (ty - n.y) * Math.min(1, dt * (st === 2 ? 4 : 1.5));
      n.m.position.y = n.y;
      n.m.visible = n.y > this.seaY - 0.5;
      if (st === 1)
        for (let i = 0; i < n.cells.length; i += 1) {
          const c = n.cells[i] as CazaPt;
          this.glow.add(c.x * K, 0.25, c.y * K, RED, 1.3 + pulse * 0.6, 0.35 + 0.35 * pulse);
        }
    });
    const c = w.crack;
    const key = c ? `${c.at.x},${c.at.y},${c.dir},${c.open}` : '';
    if (key !== this.crackKey && this.crackStrip) {
      this.crackKey = key;
      if (c) {
        const pts = c.cells.map((id) => {
          const p = cellCenter(this.map.radius, id);
          return new THREE.Vector3(p.x * K, 0, p.y * K);
        });
        // Along the line: sorted by its direction.
        const ax = pts[0] ? pts.reduce((m, p) => m + p.x, 0) / pts.length : 0;
        const az = pts[0] ? pts.reduce((m, p) => m + p.z, 0) / pts.length : 0;
        const a = (Math.PI / 3) * c.dir;
        pts.sort((p, q) => (p.x - ax) * Math.cos(a) + (p.z - az) * Math.sin(a) - ((q.x - ax) * Math.cos(a) + (q.z - az) * Math.sin(a)));
        const ext = (p: THREE.Vector3 | undefined, sgn: number): THREE.Vector3 | null => (p ? p.clone().add(new THREE.Vector3(Math.cos(a) * 0.5 * S * sgn, 0, Math.sin(a) * 0.5 * S * sgn)) : null);
        const e0 = ext(pts[0], -1);
        const e1 = ext(pts[pts.length - 1], 1);
        const all = [...(e0 ? [e0] : []), ...pts, ...(e1 ? [e1] : [])];
        this.strip(this.crackStrip, all, c.open ? 0.42 * S : 0.06 * S, c.at.x + c.at.y);
        (this.crackStrip.material as THREE.MeshBasicMaterial).color.set(c.open ? '#0f2633' : '#ff4f3a');
        this.crackStrip.visible = true;
      } else this.crackStrip.visible = false;
    }
    if (c && !c.open && s) {
      (this.crackStrip?.material as THREE.MeshBasicMaterial).opacity = 0.55 + 0.45 * pulse;
      for (const id of c.cells) {
        const p = cellCenter(this.map.radius, id);
        this.glow.add(p.x * K, 0.2, p.y * K, RED, 1.1 + pulse * 0.6, 0.3 + 0.4 * pulse);
      }
    } else if (c && this.crackStrip) (this.crackStrip.material as THREE.MeshBasicMaterial).opacity = 0.95;
    // Ivar's hunters at it, far off.
    if (this.hunters && now > this.nextChip) {
      this.nextChip = now + 0.7 + this.rnd() * 0.8;
      this.ctx.vfx.burst(this.hunters.position.clone().setY(this.seaY + 0.6), { n: 4, color: '#e8f0f6', speed: 0.03, up: 0.06, size: 0.1, life: 16, gravity: 0.008 });
    }
  }

  /** Act 2: the crossings read glinting a moment; found, the lagoon cracks and her last walk burns red under the ice. */
  private frameLagoon(now: number): void {
    this.seenAt = this.seenAt.filter((x) => now - x.t < 1.6);
    for (const x of this.seenAt) this.glow.add(x.p.x, x.p.y, x.p.z, COLD, 2.6, (1.6 - (now - x.t)) * 0.45);
    if (this.redPlane && this.foundAt >= 0) {
      const t = now - this.foundAt;
      // It runs out from the end to the whole walk, then breathes.
      const m = this.redPlane.material as THREE.MeshBasicMaterial;
      m.opacity = t < 1.2 ? t / 1.2 : 0.75 + 0.25 * Math.sin(t * 2.2);
      if (t < 2.5 && Math.floor(now * 8) !== Math.floor((now - 0.016) * 8) && this.lag) {
        const tr = this.lag.tracks.find((x) => x.who === 0 && x.letter === this.lag?.last);
        const p = tr?.pts[Math.floor(this.rnd() * (tr?.pts.length ?? 1))];
        if (p) this.ctx.vfx.ring(this.v3.set(p.x * K, 0.08, p.y * K).clone(), 0.3, 2.2, '#ff8a6e', 18, 0.7);
      }
    }
  }

  /** Act 3: the fire (out for good once out), the Seal's line marked red, the ice it cut, the eyes, the Grey Wolf. */
  private frameNight(now: number, dt: number): void {
    const s = this.last;
    const r = s?.rules?.senda?.isla?.seal;
    const w = s?.senda?.isla?.seal;
    if (this.sea) {
      const dp = (this.sea.uniforms.darkP as THREE.IUniform<THREE.Vector3[]>).value;
      for (let i = 0; i < 3; i++) {
        const d = dp[i] as THREE.Vector3;
        d.z += (1 - d.z) * Math.min(1, dt * 0.8);
      }
    }
    if (s && r && w) {
      const fire = s.senda?.npcs.find((n) => n.id === 'fire');
      if (fire && fire.hp <= 0) this.fireOut = true;
      if (this.slideStrip) {
        if (w.phase === 1) {
          const a = this.at(w.from);
          const b = this.at(w.to);
          const d = b.clone().sub(a);
          this.slideStrip.position.set(a.x, 0.07, a.z);
          this.slideStrip.rotation.y = Math.atan2(-d.x, -d.z);
          this.slideStrip.scale.set(r.slide.width * 2 * K, 1, d.length());
          this.slideStrip.visible = true;
          const k = 1 - Math.max(0, (w.until - s.tick) / Math.max(1, r.slide.warn));
          (this.slideStrip.material as THREE.MeshBasicMaterial).opacity = 0.22 + 0.3 * k + 0.15 * Math.sin(now * 14);
          for (let i = 1; i < 8; i++) this.glow.add(a.x + (d.x * i) / 8, 0.25, a.z + (d.z * i) / 8, RED, 1.2 + k, 0.3 + 0.3 * k);
        } else this.slideStrip.visible = false;
      }
      if (w.phase === 2 && Math.floor(now * 12) !== Math.floor((now - dt) * 12)) {
        const e = s.enemies.find((x) => x.id === w.id);
        const p = e ? this.ctx.pos(e.id) : undefined;
        if (p) this.ctx.vfx.burst(p.clone().setY(0.2), { n: 5, color: '#e8f0f6', speed: 0.05, up: 0.05, size: 0.12, life: 18, gravity: 0.006, spread: 0.6 });
      }
      const ck = w.cutUntil >= 0 ? `${w.from.x},${w.from.y},${w.to.x},${w.to.y}` : '';
      if (ck !== this.cutKey && this.cutStrip) {
        this.cutKey = ck;
        if (ck) {
          const a = this.at(w.from);
          const b = this.at(w.to);
          const pts: THREE.Vector3[] = [];
          for (let i = 0; i <= 10; i++) pts.push(a.clone().lerp(b, i / 10));
          this.strip(this.cutStrip, pts, 0.32 * S, w.slides * 13 + 1);
          this.cutStrip.visible = true;
        } else this.cutStrip.visible = false;
      }
      if (this.cutStrip?.visible && w.cutUntil >= 0) (this.cutStrip.material as THREE.MeshBasicMaterial).opacity = Math.min(0.9, Math.max(0.2, (w.cutUntil - s.tick) / 24));
      // The crack it goes for, burning red.
      if ((w.phase === 3 || w.phase === 4) && w.hole >= 0) {
        const h = r.holes[w.hole] as CazaPt;
        const pulse = 0.5 + 0.5 * Math.sin(now * 12);
        this.glow.add(h.x * K, 0.4, h.y * K, RED, 2.6 + pulse * 1.2, 0.5 + 0.4 * pulse);
      }
    }
    for (const [i, e] of this.eyes.entries()) {
      const blink = Math.sin(now * 0.7 + i * 1.9) > -0.85 ? 1 : 0;
      if (!blink) continue;
      for (const k of [-1, 1]) this.glow.add(e.x + k * 0.09, e.y, e.z, RED, 0.22, 0.85);
    }
    if (this.gray && this.hour === 'dawn') {
      // The memory's dawn: it swims off north toward the black rock.
      const g = this.gray.position;
      if (g.z > -20) g.set(-4, this.seaY, -22);
      g.z -= dt * 1.4;
      g.x += (-12 - g.x) * Math.min(1, dt * 0.05);
      g.y = this.seaY + Math.sin(now * 1.6) * 0.1;
      this.gray.rotation.y = Math.PI;
      if (Math.floor(now * 3) !== Math.floor((now - dt) * 3)) this.ctx.vfx.burst(g.clone().setY(this.seaY + 0.1), { n: 3, color: '#c9d2dc', speed: 0.02, up: 0.02, size: 0.35, life: 40, smoke: true, spread: 0.6 });
    } else if (this.gray) {
      const a = now * 0.06;
      this.gray.position.set(Math.cos(a) * 30, this.seaY + Math.sin(now * 1.6) * 0.1, Math.sin(a) * 24);
      this.gray.rotation.y = -a;
    }
  }

  frame(now: number, camera?: THREE.Camera, alpha = 1): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (camera) lookPoint(camera, this.look);
    this.snow?.frame(now, this.look);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    if (this.aurora) {
      (this.aurora.uniforms.time as THREE.IUniform<number>).value = now;
      const k = this.aurora.uniforms.k as THREE.IUniform<number>;
      k.value += (this.auroraK - k.value) * Math.min(1, dt * 0.8);
    }
    this.drawTerron(this.last, alpha);
    this.glow.begin();
    if (this.place === 'islaFloes') this.frameFloes(now, dt);
    else if (this.place === 'islaLagoon') this.frameLagoon(now);
    else if (this.place === 'islaNight') this.frameNight(now, dt);
    if (this.boat?.visible) {
      // Sten's small boat coming in by the channel, rowed without stopping.
      this.boat.position.set(Math.cos(now * 0.25) * 0.3, Math.sin(now * 1.3) * 0.05, Math.max(-3, 2 - now * 0.12));
    }
    if (this.embers.length && !this.fireOut && now > this.nextEmber) {
      this.nextEmber = now + (this.ctx.low ? 0.3 : 0.15);
      const e = this.embers[Math.floor(this.rnd() * this.embers.length)] as THREE.Vector3;
      this.ctx.vfx.burst(e, { n: 1, color: '#ff9a5a', speed: 0.006, up: 0.03, size: 0.05, life: 30, gravity: -0.0008, spread: 0.2 });
    }
    for (const l of this.lights) {
      if (l.fire && this.fireOut) continue;
      const fl = l.flick ? 0.84 + 0.16 * Math.sin(now * (6 + l.seed) + l.seed) * l.flick + 0.06 * Math.sin(now * 17 + l.seed * 3) * l.flick : 1;
      if (l.a > 0) this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * fl);
    }
    // The aurora's green on the ice, faint, under the sky.
    if (this.auroraK > 0.5) this.glow.add(this.look.x, this.look.y + 10, this.look.z - 20, AUR, 40, 0.03);
    this.glow.end();
    void RED_SOFT;
  }

  dispose(): void {
    for (let i = 0; i < this.usesIce; i++) releaseIceTexture();
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    for (const t of this.texs) t.dispose();
    this.dome?.dispose();
    this.snow?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}

